import { useEffect, useState, type DependencyList } from "react";

function depsEqual(a: DependencyList, b: DependencyList): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

/**
 * Defers mounting an expensive-to-render Table for one paint cycle, so
 * navigating to a step with a large, already-populated table shows a
 * lightweight spinner immediately instead of the tab appearing to freeze
 * while the table's first synchronous render/layout blocks the main thread.
 *
 * Returns `true` for one requestAnimationFrame + one macrotask after mount
 * (or after any value in `deps` changes), then `false` for the rest of that
 * mount. With the default `deps` (`[]`) this fires exactly once per mount -
 * it never re-triggers on ordinary data edits, so typing into the table
 * never re-shows the loader. Pass `deps` only when the step itself remounts
 * a genuinely different large dataset without unmounting (e.g. switching
 * between two big tabs of the same component).
 *
 * The reset on a `deps` change happens synchronously *during render*,
 * comparing `deps` against the previous render's value and calling
 * `setIsDeferring(true)` right there in the render body when they differ.
 * This is React's own documented pattern for "adjusting state when a prop
 * changes" (react.dev) - React discards this render and immediately
 * re-renders with the updated state before committing anything to the DOM,
 * so the browser never paints the intermediate (unwanted) result.
 *
 * This has already gone through three wrong turns in this codebase - do not
 * reintroduce any of them:
 *
 * 1. Resetting from a `useEffect` keyed on `deps` instead of during render:
 *    an effect only runs after React commits the render where `deps`
 *    changed, so that render still reads the stale `false`, mounts the
 *    expensive Table synchronously (the exact freeze this hook exists to
 *    prevent), and only then gets torn down again once the effect catches
 *    up - a visible stutter plus a wasted double mount on every switch.
 * 2. Tracking "is deferring" in a plain `ref` and reading `ref.current`
 *    directly in the render's return value: this violates the Rules of
 *    Hooks and is explicitly incompatible with the React Compiler this
 *    project uses (`CLAUDE.md` §2) - the compiler memoizes JSX based on
 *    tracked reactive inputs (state/props/context), and a ref read isn't
 *    one, so it's free to serve back stale/cached JSX across renders even
 *    though the ref's value changed.
 * 3. Tracking the *previous* `deps` value (for the render-time comparison
 *    above) in a `ref` instead of `useState`, while still using `useState`
 *    for `isDeferring` itself: writing to a ref during render is unsafe for
 *    a subtler reason than #2 - React (Strict Mode in development, and
 *    Concurrent React generally) can invoke a component's render function
 *    twice for one logical render. The first, throwaway invocation already
 *    mutates the ref to the new `deps` value; by the second, "real"
 *    invocation the comparison then sees "no change" (the ref was already
 *    updated by the throwaway pass) and silently skips resetting
 *    `isDeferring` - the loader just never reappears. Comparing against a
 *    `useState` value instead doesn't have this problem, because state
 *    updates issued during a throwaway render are correctly
 *    discarded/replayed by React's reconciler rather than leaking through a
 *    mutable side channel that survives between the two invocations.
 */
export function useTableRenderDeferral(deps: DependencyList = []): boolean {
  const [prevDeps, setPrevDeps] = useState(deps);
  const [isDeferring, setIsDeferring] = useState(true);

  if (!depsEqual(deps, prevDeps)) {
    setPrevDeps(deps);
    setIsDeferring(true);
  }

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => setIsDeferring(false), 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return isDeferring;
}
